import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Button,
  NavigationTree,
  NavigationTreeItem,
  NavigationTreeItemContent,
  type Key,
} from "react-aria-components";
import { FormattedMessage, useIntl } from "react-intl";
import { useAnnounce } from "../components/Announcer.tsx";
import { errorMessage } from "../errors.ts";
import {
  atLeast,
  childrenMap,
  createPage,
  refreshWorkspace,
  teamspacesQuery,
  treeQuery,
  type Section,
  type Teamspace,
  type TreePage,
  type Workspace,
} from "./data.ts";

import { PageLabel } from "./PageLabel.tsx";
import { SwitcherButton } from "./SwitcherButton.tsx";
import { isApple, shortcutLabel, useCommands } from "./quickfind.ts";
import { withMessages } from "../i18n.ts";

/** Loaded just after the page shows (startup budget, doc 05); its commands register then. */
const PageActions = lazy(() =>
  import("./PageActions.tsx").then((m) => ({ default: m.PageActions })),
);

/** Imports (ADR-0073) load when the dialog opens. */
const ImportDialog = lazy(
  withMessages(() =>
    import("../import/ImportDialog.tsx").then((m) => ({ default: m.ImportDialog })),
  ),
);

const SwitcherMenu = lazy(
  withMessages(() => import("./SwitcherMenu.tsx").then((m) => ({ default: m.SwitcherMenu }))),
);

export const pageHref = (workspaceId: string, pageId: string) => `/w/${workspaceId}/p/${pageId}`;

const storageKey = (workspaceId: string) => `paginae:expanded:${workspaceId}`;

/** Expanded tree rows are remembered per browser, as a convenience only. */
function loadExpanded(workspaceId: string): Set<Key> {
  try {
    const raw = localStorage.getItem(storageKey(workspaceId));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveExpanded(workspaceId: string, keys: Set<Key>) {
  try {
    localStorage.setItem(storageKey(workspaceId), JSON.stringify([...keys]));
  } catch {
    // Storage may be unavailable (private mode, blocked site data); the tree still works.
  }
}

export function Sidebar({
  workspace,
  onSearch,
  toggle,
}: {
  workspace: Workspace;
  onSearch: () => void;
  /** The button that hides the sidebar (ADR-0087). */
  toggle?: ReactNode;
}) {
  const intl = useIntl();
  const tree = useQuery(treeQuery(workspace.id));
  // The teamspaces the viewer belongs to have sections of their own (ADR-0030).
  const teamspaces = useQuery({
    ...teamspacesQuery(workspace.id),
    enabled: workspace.role !== null,
  });
  const mine = (teamspaces.data ?? []).filter((t) => t.role !== null && !t.archived);
  const location = useLocation();
  const apple = isApple();
  const [expanded, setExpanded] = useState<Set<Key>>(() => loadExpanded(workspace.id));
  const [importing, setImporting] = useState(false);
  const [switching, setSwitching] = useState(false);
  useCommands(
    "import",
    workspace.role !== null
      ? [
          {
            id: "import",
            label: intl.formatMessage({ id: "command.import" }),
            keywords: [
              "import",
              "notion",
              "markdown",
              "html",
              "word",
              "confluence",
              "evernote",
              "trello",
              "csv",
            ],
            run: () => setImporting(true),
          },
        ]
      : [],
  );

  // Keep the current page visible: when it changes, expand its ancestors once (state adjusted
  // during render, React's pattern for state derived from a changed value).
  const currentId = /\/p\/([^/]+)/.exec(location.pathname)?.[1] ?? null;
  const pages = tree.data;
  const [revealed, setRevealed] = useState<string | null>(null);
  if (pages && currentId && revealed !== currentId) {
    setRevealed(currentId);
    const byId = new Map(pages.map((p) => [p.id, p]));
    const ancestors: string[] = [];
    for (let p = byId.get(currentId); p?.parentId; p = byId.get(p.parentId)) {
      ancestors.push(p.parentId);
    }
    if (ancestors.some((id) => !expanded.has(id))) {
      // Not persisted here, to keep rendering free of side effects; the next toggle saves it.
      setExpanded(new Set([...expanded, ...ancestors]));
    }
  }

  function onExpandedChange(keys: Set<Key>) {
    setExpanded(keys);
    saveExpanded(workspace.id, keys);
  }

  function expand(id: string) {
    onExpandedChange(new Set([...expanded, id]));
  }

  return (
    <nav className="sidebar" aria-label={intl.formatMessage({ id: "sidebar.label" })}>
      <div className="sidebar-top">
        {/* The workspace switcher (ADR-0098): its menu loads just after the sidebar. */}
        {/* Pressed before its menu has loaded, it opens once it has. */}
        <Suspense
          fallback={<SwitcherButton workspace={workspace} onPress={() => setSwitching(true)} />}
        >
          <SwitcherMenu workspace={workspace} defaultOpen={switching} />
        </Suspense>
        {toggle}
      </div>
      <ul className="sidebar-links">
        <li>
          <Button
            className="sidebar-search"
            onPress={onSearch}
            aria-keyshortcuts={apple ? "Meta+K Meta+P" : "Control+K Control+P"}
          >
            <FormattedMessage id="sidebar.search" />
            <kbd aria-hidden="true">{shortcutLabel(["Mod", "K"], apple)}</kbd>
          </Button>
        </li>
        <li>
          <Link
            to="/w/$workspaceId"
            params={{ workspaceId: workspace.id }}
            activeOptions={{ exact: true }}
            activeProps={{ "aria-current": "page" }}
          >
            <FormattedMessage id="sidebar.home" />
          </Link>
        </li>
        <li>
          <Link to="/shared" activeProps={{ "aria-current": "page" }}>
            <FormattedMessage id="sidebar.sharedWithMe" />
          </Link>
        </li>
      </ul>
      {tree.isError && <p role="alert">{errorMessage(intl, tree.error)}</p>}
      {tree.data && (
        <>
          {tree.data.some((p) => p.section === "shared") && (
            <SidebarSection
              workspaceId={workspace.id}
              section="shared"
              title={intl.formatMessage({ id: "sidebar.shared" })}
              pages={tree.data}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              onExpand={expand}
            />
          )}
          {workspace.role !== "guest" && workspace.role !== null && (
            <SidebarSection
              workspaceId={workspace.id}
              section="workspace"
              title={workspace.name}
              pages={tree.data}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              onExpand={expand}
            />
          )}
          {mine.map((team) => (
            <SidebarSection
              key={team.id}
              workspaceId={workspace.id}
              section="teamspace"
              teamspace={team}
              title={team.name}
              pages={tree.data}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              onExpand={expand}
            />
          ))}
          {workspace.role !== null && (
            <SidebarSection
              workspaceId={workspace.id}
              section="private"
              title={intl.formatMessage({ id: "sidebar.private" })}
              pages={tree.data}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
              onExpand={expand}
            />
          )}
        </>
      )}
      {workspace.role !== null && (
        <ul className="sidebar-links">
          <li>
            <Link
              to="/w/$workspaceId/library"
              params={{ workspaceId: workspace.id }}
              activeProps={{ "aria-current": "page" }}
            >
              <FormattedMessage id="sidebar.library" />
            </Link>
          </li>
          <li>
            <Link
              to="/w/$workspaceId/files"
              params={{ workspaceId: workspace.id }}
              activeProps={{ "aria-current": "page" }}
            >
              <FormattedMessage id="sidebar.files" />
            </Link>
          </li>
          <li>
            <Link
              to="/w/$workspaceId/people"
              params={{ workspaceId: workspace.id }}
              activeProps={{ "aria-current": "page" }}
            >
              <FormattedMessage id="sidebar.people" />
            </Link>
          </li>
          <li>
            <Link
              to="/w/$workspaceId/teamspaces"
              params={{ workspaceId: workspace.id }}
              activeOptions={{ exact: true }}
              activeProps={{ "aria-current": "page" }}
            >
              <FormattedMessage id="sidebar.teamspaces" />
            </Link>
          </li>
          <li>
            <Link
              to="/w/$workspaceId/archive"
              params={{ workspaceId: workspace.id }}
              activeProps={{ "aria-current": "page" }}
            >
              <FormattedMessage id="sidebar.archive" />
            </Link>
          </li>
          <li>
            <Link
              to="/w/$workspaceId/trash"
              params={{ workspaceId: workspace.id }}
              activeProps={{ "aria-current": "page" }}
            >
              <FormattedMessage id="sidebar.trash" />
            </Link>
          </li>
          {workspace.role !== null && (
            <li>
              <Button className="sidebar-button" onPress={() => setImporting(true)}>
                <FormattedMessage id="sidebar.import" />
              </Button>
            </li>
          )}
          {workspace.role === "owner" && (
            <li>
              <Link
                to="/w/$workspaceId/settings"
                params={{ workspaceId: workspace.id }}
                activeProps={{ "aria-current": "page" }}
              >
                <FormattedMessage id="sidebar.settings" />
              </Link>
            </li>
          )}
        </ul>
      )}
      {importing && (
        <Suspense fallback={null}>
          <ImportDialog
            workspaceId={workspace.id}
            pageId={currentId}
            onClose={() => setImporting(false)}
          />
        </Suspense>
      )}
    </nav>
  );
}

function SidebarSection(props: {
  workspaceId: string;
  section: Section;
  /** The teamspace, for a teamspace's section. */
  teamspace?: Teamspace;
  title: string;
  pages: TreePage[];
  expanded: Set<Key>;
  onExpandedChange: (keys: Set<Key>) => void;
  onExpand: (id: string) => void;
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const announce = useAnnounce();
  const location = useLocation();
  const teamId = props.teamspace?.id ?? null;
  const children = useMemo(
    () => childrenMap(props.pages, props.section, teamId),
    [props.pages, props.section, teamId],
  );
  const headingId = `sidebar-${props.section}${teamId ? `-${teamId}` : ""}`;
  const byId = useMemo(() => new Map(props.pages.map((p) => [p.id, p])), [props.pages]);

  // Dragging a page among its siblings, or onto another page to put it inside (ADR-0087), with
  // the browser's own drag and drop, set up after the first render. The page's menu has "Move
  // up", "Move down" and "Move to…" for the keyboard.
  const canDrag = props.section !== "shared";
  const sectionRef = useRef<HTMLElement>(null);
  const latestDrop = useRef<typeof dropped | null>(null);
  useEffect(() => {
    const element = sectionRef.current;
    if (!canDrag || !element) return;
    let detach: (() => void) | null = null;
    let live = true;
    void import("./sidebar-drag.ts").then((m) => {
      if (live) detach = m.attachDrag(element, (...args) => void latestDrop.current?.(...args));
    });
    return () => {
      live = false;
      detach?.();
    };
  }, [canDrag]);
  async function dropped(pageId: string, targetId: string, at: "before" | "after" | "on") {
    const page = byId.get(pageId);
    const target = byId.get(targetId);
    if (!page || !target || page.id === target.id) return;
    try {
      const { dropDestination, movePage } = await import("./page-moves.ts");
      const to = dropDestination(props.pages, page, target, at);
      await movePage(page, to);
      if (to.parentId) props.onExpand(to.parentId);
      await refreshWorkspace(queryClient, props.workspaceId);
      announce(
        intl.formatMessage(
          { id: "sidebar.moved" },
          { title: page.title || intl.formatMessage({ id: "page.untitled" }) },
        ),
      );
    } catch (err) {
      announce(errorMessage(intl, err));
    }
  }
  useEffect(() => {
    latestDrop.current = dropped;
  });
  // In a teamspace, its owners and members who may edit its pages add at the top.
  const canAdd =
    props.section !== "shared" &&
    (!props.teamspace ||
      props.teamspace.role === "owner" ||
      atLeast(props.teamspace.memberLevel, "can_edit"));

  async function addPage(parentId: string | null) {
    try {
      const id = await createPage(
        props.workspaceId,
        props.section === "shared"
          ? { parentId }
          : parentId === null && teamId
            ? { parentId, section: "teamspace", teamspaceId: teamId }
            : { parentId, section: props.section },
      );
      if (parentId) props.onExpand(parentId);
      await refreshWorkspace(queryClient, props.workspaceId);
      await navigate({
        to: "/w/$workspaceId/p/$pageId",
        params: { workspaceId: props.workspaceId, pageId: id },
        search: { new: true },
      });
    } catch (err) {
      announce(errorMessage(intl, err));
    }
  }

  const renderItem = (page: TreePage): ReactNode => {
    const kids = children.get(page.id) ?? [];
    const label = page.title || intl.formatMessage({ id: "page.untitled" });
    return (
      <NavigationTreeItem
        key={page.id}
        id={page.id}
        href={pageHref(props.workspaceId, page.id)}
        textValue={label}
        className="tree-item"
        hasChildItems={kids.length > 0}
      >
        <NavigationTreeItemContent>
          {({ hasChildItems, isExpanded }) => (
            <div
              className="tree-row"
              data-page-id={page.id}
              draggable={canDrag && atLeast(page.level, "full_access")}
            >
              {hasChildItems ? (
                <Button slot="chevron" className="tree-chevron">
                  <span aria-hidden="true">{isExpanded ? "▾" : "▸"}</span>
                </Button>
              ) : (
                <span className="tree-chevron" aria-hidden="true" />
              )}
              <PageLabel title={page.title} icon={page.icon} className="tree-label" />
              <Suspense fallback={null}>
                <PageActions
                  workspaceId={props.workspaceId}
                  page={page}
                  pages={props.pages}
                  onAddSubpage={() => void addPage(page.id)}
                />
              </Suspense>
            </div>
          )}
        </NavigationTreeItemContent>
        {kids.map(renderItem)}
      </NavigationTreeItem>
    );
  };

  const roots = children.get(null) ?? [];
  return (
    <section ref={sectionRef} className="sidebar-section" aria-labelledby={headingId}>
      <div className="sidebar-section-header">
        <h2 id={headingId}>
          {props.teamspace?.icon && <span aria-hidden="true">{props.teamspace.icon} </span>}
          {props.title}
        </h2>
        {props.teamspace && (
          <Link
            to="/w/$workspaceId/teamspaces/$teamspaceId"
            params={{ workspaceId: props.workspaceId, teamspaceId: props.teamspace.id }}
            className="button button-quiet button-small"
            aria-label={intl.formatMessage(
              { id: "sidebar.teamspaceSettings" },
              { name: props.title },
            )}
          >
            <span aria-hidden="true">⚙</span>
          </Link>
        )}
        {/* Shared pages are added to from inside them, not at the top of the section. */}
        {canAdd && (
          <Button
            className="button button-quiet button-small"
            onPress={() => void addPage(null)}
            aria-label={intl.formatMessage({ id: "sidebar.addPageIn" }, { section: props.title })}
          >
            <span aria-hidden="true">+</span>
          </Button>
        )}
      </div>
      {roots.length === 0 ? (
        <p className="help">
          <FormattedMessage id="sidebar.empty" />
        </p>
      ) : (
        <NavigationTree
          aria-labelledby={headingId}
          className="tree"
          selectedRoute={location.pathname}
          expandedKeys={props.expanded}
          onExpandedChange={props.onExpandedChange}
        >
          {roots.map(renderItem)}
        </NavigationTree>
      )}
    </section>
  );
}
